import { type KeyboardEvent, type MouseEvent, type ReactNode } from 'react';

import Button from '@/components/Button/Button';
import Spinner from '@/components/Spinner/Spinner';
import { cn } from '@/lib/cn';

/**
 * Retained for the stocks tile-density config; the shell no longer maps size to
 * a grid span (placement is owned by the fixed layout in DashboardGrid).
 */
export type WidgetSize = '1x1' | '2x1' | '2x2' | '3x1' | '3x2';
export type WidgetStatus = 'loading' | 'error' | 'empty' | 'loaded';

interface WidgetProps {
  /** Small uppercase label at the top-left. Omit for label-less cards (e.g. weather). */
  title?: string;
  /** Muted accessory shown to the right of the label (e.g. "walk 7 min"). */
  titleAccessory?: ReactNode;
  status: WidgetStatus;
  errorMessage?: string;
  emptyMessage?: string;
  loadingMessage?: string;
  onOpenDetail?: () => void;
  children?: ReactNode;
  className?: string;
  /** Set to false to disable the “whole-tile is clickable” affordance even when onOpenDetail is provided. */
  bodyOpensDetail?: boolean;
  /** When true, replaces the body with a setup placeholder + Configure button. */
  needsSetup?: boolean;
  setupMessage?: string;
  onConfigure?: () => void;
  configureLabel?: string;
}

const ChevronIcon = (
  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden="true" className="h-4 w-4">
    <path d="M8 5l5 5-5 5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export default function Widget({
  title,
  titleAccessory,
  status,
  errorMessage,
  emptyMessage,
  loadingMessage,
  onOpenDetail,
  children,
  className,
  bodyOpensDetail = true,
  needsSetup = false,
  setupMessage,
  onConfigure,
  configureLabel = 'Configure',
}: WidgetProps) {
  const clickable = !!onOpenDetail && bodyOpensDetail && !needsSetup;
  const showChevron = !!onOpenDetail && !needsSetup;

  const handleBodyClick = (e: MouseEvent<HTMLDivElement>) => {
    if (!clickable) return;
    // Skip if a child interactive element (not the body itself) absorbed the click.
    const target = e.target as HTMLElement;
    const interactive = target.closest('button, a, input, select, textarea, [role="button"]');
    if (interactive && interactive !== e.currentTarget) return;
    onOpenDetail!();
  };

  const handleBodyKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (!clickable) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onOpenDetail!();
    }
  };

  return (
    <section
      className={cn(
        'group relative flex min-h-0 flex-col overflow-hidden rounded-[18px] border border-(--border-subtle) p-5 shadow-(--shadow-widget) transition-colors',
        clickable && 'cursor-pointer hover:border-(--border-default)',
        className,
      )}
      style={{ background: 'var(--gradient-surface, var(--bg-surface))' }}
      aria-busy={status === 'loading' || undefined}
    >
      {showChevron && (
        <span className="pointer-events-none absolute top-4 right-4 z-10 text-(--text-muted) transition-colors group-hover:text-(--text-tertiary)">
          {ChevronIcon}
        </span>
      )}

      {(title || titleAccessory) && (
        <header className="mb-3 flex items-baseline gap-2 pr-6">
          {title && <h2 className="truncate text-[11px] font-semibold tracking-[0.14em] text-(--text-tertiary) uppercase">{title}</h2>}
          {titleAccessory && <span className="truncate text-xs text-(--text-muted)">{titleAccessory}</span>}
        </header>
      )}

      <div
        className="min-h-0 flex-1"
        onClick={handleBodyClick}
        onKeyDown={handleBodyKey}
        role={clickable ? 'button' : undefined}
        tabIndex={clickable ? 0 : undefined}
        aria-label={clickable ? `Open ${title ?? 'widget'} details` : undefined}
      >
        {needsSetup ? (
          <div className="flex h-full flex-col items-start justify-center gap-3">
            <p className="text-sm text-(--text-tertiary)">{setupMessage ?? 'Not configured yet.'}</p>
            {onConfigure && (
              <Button variant="secondary" size="sm" onClick={onConfigure}>
                {configureLabel}
              </Button>
            )}
          </div>
        ) : status === 'loading' ? (
          <div className="flex h-full items-center gap-3 text-sm text-(--text-tertiary)">
            <Spinner size="sm" />
            <span>{loadingMessage ?? 'Loading…'}</span>
          </div>
        ) : status === 'error' ? (
          <div className="text-sm text-(--accent-danger)">{errorMessage ?? 'Something went wrong.'}</div>
        ) : status === 'empty' ? (
          <div className="text-sm text-(--text-tertiary)">{emptyMessage ?? 'Nothing here.'}</div>
        ) : (
          children
        )}
      </div>
    </section>
  );
}
